import React, { useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import invalid from '../../../assets/images/invalid.svg'

/**
 * InputAutocomplete component renders an input field with autocomplete suggestions.
 * It allows users to filter and select from a list of suggestions based on their input.
 *
 * @module form/InputAutocomplete
 * @description InputAutocomplete component renders an input field with autocomplete suggestions.
 * It allows users to filter and select from a list of suggestions based on their input.
 * @param {Array<Object>} suggestions - List of suggestions to be displayed for autocomplete.
 * @param {Object} field - Field information passed by react-hook-form or similar.
 * @param {Object} errors - Validation errors object, usually provided by react-hook-form.
 * @param {Function} register - Function to register the input field, usually provided by react-hook-form.
 *
 * @example
 * const suggestions = [{ id: 1, name: 'Apple' }, { id: 2, name: 'Banana' }];
 * const field = { name: 'fruit' };
 * const errors = {};
 * const register = () => {};
 *
 * <InputAutocomplete suggestions={suggestions} field={field} errors={errors} register={register} />
 *
 * @returns {React.Element} The rendered InputAutocomplete component.
 */
export const InputAutocomplete = ({ suggestions = [], field, errors, register }) => {
  const [activeSuggestion, setActiveSuggestion] = useState(0)
  const [filteredSuggestions, setFilteredSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [userInput, setUserInput] = useState('')

  /**
   * Handles the input change event and filters the suggestions list.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event.
   */
  const onChange = (e) => {
    const userInput = e.currentTarget.value

    const filteredSuggestions = suggestions.filter((suggestion) =>
      suggestion.name.toLowerCase().includes(userInput.toLowerCase()),
    )

    setActiveSuggestion(0)
    setFilteredSuggestions(filteredSuggestions)
    setShowSuggestions(true)
    setUserInput(userInput)
  }

  /**
   * Handles the click event on a suggestion and sets the input value.
   *
   * @param {React.MouseEvent<HTMLLIElement>} e - The click event.
   */
  const onClick = (e) => {
    setActiveSuggestion(0)
    setFilteredSuggestions([])
    setShowSuggestions(false)
    setUserInput(e.currentTarget.innerText)
  }

  /**
   * Handles keyboard navigation and selection of suggestions.
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} e - The keydown event.
   */
  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      setUserInput(filteredSuggestions[activeSuggestion].name)
      setActiveSuggestion(0)
      setShowSuggestions(false)
    } else if (e.key === 'ArrowUp') {
      if (activeSuggestion === 0) return
      setActiveSuggestion(activeSuggestion - 1)
    } else if (e.key === 'ArrowDown') {
      if (activeSuggestion + 1 === filteredSuggestions.length) return
      setActiveSuggestion(activeSuggestion + 1)
    }
  }

  let suggestionsListComponent

  if (showSuggestions && userInput) {
    if (filteredSuggestions.length) {
      suggestionsListComponent = (
        <ul className="suggestions">
          {filteredSuggestions.map((suggestion, index) => {
            let className = index === activeSuggestion ? 'suggestion-active' : ''
            return (
              <li className={className} key={suggestion.id} onClick={onClick}>
                {suggestion.name}
              </li>
            )
          })}
        </ul>
      )
    } else {
      suggestionsListComponent = (
        <div className="no-suggestions">
          <em>No suggestions, you&apos;re on your own!</em>
        </div>
      )
    }
  }

  return (
    <Fragment>
      <input
        type="text"
        style={{
          color: '#000000',
          paddingRight: 'calc(1.5em + 1rem)',
          backgroundImage: errors[field.name] && `url("${invalid}")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right calc(0.375em + 0.25rem) center',
          backgroundSize: 'calc(0.75em + 0.5rem) calc(0.75em + 0.5rem)',
        }}
        onKeyDown={onKeyDown}
        value={userInput}
        {...register(field.name, { onChange: onChange })}
        className={`trtui-form-control ${errors[field.name] && 'trtui-is-invalid'}`}
        autoComplete="off"
        data-testid="inputAutocomplete"
      />
      {suggestionsListComponent}
    </Fragment>
  )
}

InputAutocomplete.propTypes = {
  /**
   * List of suggestions for the autocomplete.
   */
  suggestions: PropTypes.arrayOf(PropTypes.object),

  /**
   * Field information, usually provided by react-hook-form.
   */
  field: PropTypes.object.isRequired,

  /**
   * Validation errors, usually provided by react-hook-form.
   */
  errors: PropTypes.object.isRequired,

  /**
   * Function to register the input field, usually provided by react-hook-form.
   */
  register: PropTypes.func.isRequired,
}

InputAutocomplete.defaultProps = {
  suggestions: [],
}
